import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Box, Typography, Grid, useMediaQuery } from "@mui/material";
import {
  MessageCircle,
  Search,
  BookOpen,
  Award,
  FileText,
  Edit3,
  File,
  Folder,
  BarChart2
} from 'react-feather';

const HomeMainPage = () => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const handleCardClick = (route) => {
    if (route === "/app/case-management") return;
    if (route === "/app/legal-doc") return;
    navigate(route);
  };

  return (
    <>
      <Helmet>
        <title>LawGPT - Home</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          backgroundColor: "#F8FAFC",
          p: { xs: 3, md: 4 },
          pt: { xs: 12, md: 16 }, // Add top padding to account for navbar
          pb: { xs: 3, md: 4 }, // Add bottom padding to match sides
          flex: 1,
          overflowY: "auto",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            mb: 0, // Reduced from 8 to 4 to bring cards closer
            mt: 0,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: { xs: "24px", sm: "28px", md: "32px" },
                fontFamily: "'Inter', 'Inter Fallback', sans-serif",
              }}
            >
              Hello, {userInfo?.name}
            </Typography>
            <Box
              component="span"
              sx={{
                fontSize: { xs: "24px", sm: "28px", md: "32px" },
                animation: "wave 2s infinite",
                display: "inline-block",
                transformOrigin: "70% 70%",
                "@keyframes wave": {
                  "0%": { transform: "rotate(0deg)" },
                  "10%": { transform: "rotate(14deg)" },
                  "20%": { transform: "rotate(-8deg)" },
                  "30%": { transform: "rotate(14deg)" },
                  "40%": { transform: "rotate(-4deg)" },
                  "50%": { transform: "rotate(10deg)" },
                  "60%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(0deg)" },
                },
              }}
            >
              👋
            </Box>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#6B7280",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 400,
              fontFamily: "'Inter', 'Inter Fallback', sans-serif",
              maxWidth: "600px",
            }}
          >
            Welcome to LawGPT. How can I help you today?
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            width: "100%",
            mb: 2, // Add bottom margin to prevent cards from touching bottom
          }}
        >
          <Box
            sx={{
              maxWidth: "1200px",
              width: "100%",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: "16px",
              rowGap: "16px", // Increased gap between rows
              alignContent: "flex-start", // Align content to top for better space distribution
              justifyItems: "center", // Center items horizontally
            }}
          >
            {[
              {
                label: "Legal Chat",
                text: "AI-powered legal assistance for precise responses to legal inquiries",
                icon: MessageCircle,
                route: "/app/legal-chat",
                color: "#0073A6",
                bgColor: "#F0F9FF",
              },
              {
                label: "Case Search",
                text: "Effortlessly access key case laws to instantly retrieve relevant cases",
                icon: Search,
                route: "/app/searchResults",
                color: "#059669",
                bgColor: "#F0FDF4",
              },
              {
                label: "Statute Search",
                text: "Quickly retrieve pertinent statutes to locate applicable laws seamlessly",
                icon: BookOpen,
                route: "/app/statute-search",
                color: "#DC2626",
                bgColor: "#FEF2F2",
              },
              {
                label: "JudgeGPT",
                text: "Turn evidence into verdicts with AI-driven legal analysis",
                icon: Award,
                route: "/app/judge-gpt",
                color: "#7C3AED",
                bgColor: "#F5F3FF",
              },
              {
                label: "Application Drafting",
                text: "Structured legal application creation to generate well-organized applications",
                icon: FileText,
                route: "/app/application-drafting",
                color: "#EA580C",
                bgColor: "#FFF7ED",
              },
              {
                label: "Petition Drafting",
                text: "Professional petition drafting to create accurate, high-quality legal documents",
                icon: Edit3,
                route: "/app/petition-drafting",
                color: "#059669",
                bgColor: "#F0FDF4",
              },
              {
                label: "Contract Drafting",
                text: "Reliable contract drafting to generate accurate, ready-to-use legal contracts",
                icon: File,
                route: "/app/contract-drafting",
                color: "#0284C7",
                bgColor: "#F0F9FF",
              },
              {
                label: "Case Management",
                text: "A centralized system to efficiently manage, track, and collaborate on legal cases",
                icon: Folder,
                route: "/app/case-management",
                color: "#DC2626",
                bgColor: "#FEF2F2",
              },
              {
                label: "Document Analysis",
                text: "AI-enhanced document analysis to extract insights from complex legal texts",
                icon: BarChart2,
                route: "/app/document-analysis",
                color: "#7C3AED",
                bgColor: "#F5F3FF",
              },
            ].map((item, index) => (
              <Box
                key={index}
                onClick={() => handleCardClick(item.route)}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  px: 4,
                  py: 3.5,
                  height: "180px",
                  border: "1px solid #E5E7EB",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    borderColor: item.color,
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: item.bgColor,
                    mb: 2,
                  }}
                >
                  <item.icon 
                    size={24} 
                    color={item.color}
                  />
                </Box>

                {/* Content */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#111827",
                      fontSize:{ xs: "13px", lg: "16px" },
                      mb: 1,
                      lineHeight: 1.3,
                      fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6B7280",
                      fontSize:{ xs: "12px", lg: "14px" },
                      lineHeight: { xs: "1.2", lg: "1.5" },
                      fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default HomeMainPage;
